import { ethers } from "ethers";
import { useWeb3Context } from "./web3Context";
import { abi as IERC20ABI } from "../abis/IERC20.json";
import { useCallback, useMemo } from "react";
import { clearPendingTxn, fetchPendingTxns } from "src/slices/PendingTxnsSlice";
import { useDispatch } from "react-redux";
import { serializeError } from "eth-rpc-errors";
import { toastError, toastSuccess } from "src/helpers/toastHelpers";
import { useTokenContract } from "./useContract";
import { useSingleCallResult } from "src/lib/hooks/multicall";
import { PLACEHOLDER_ACCOUNT } from "src/helpers/Helpers";
import { useUpDataBlockNumber } from "src/lib/hooks/useUpDataBlockNumber";
import { BN } from "src/utils/bn";
export default function useTokenApprove() {
  const { address, provider } = useWeb3Context();
  const dispatch = useDispatch();

  const getERC20Approved = async (
    tokenAddress: string,
    spenderAddress: string
  ) => {
    const tokenContract = new ethers.Contract(
      tokenAddress,
      IERC20ABI,
      provider
    );
    if (!tokenAddress || !spenderAddress) return false;

    const allowance = await tokenContract.allowance(
      address ? address : PLACEHOLDER_ACCOUNT,
      spenderAddress
    );
    return Number(allowance) > 0;
  };

  const approveToken = useCallback(
    async (tokenAddress: string, spenderAddress: string) => {
      if (provider && address) {
        let tx;
        try {
          const signer = provider.getSigner();
          const tokenContract = new ethers.Contract(
            tokenAddress,
            IERC20ABI,
            signer
          );
          tx = await tokenContract.approve(
            spenderAddress,
            ethers.constants.MaxUint256
          );
          dispatch(
            fetchPendingTxns({
              txnHash: tx.hash,
              text: "Approve",
              type: "approveToken",
            })
          );
          await tx.wait();
          return tx;
        } catch (e: unknown) {
          const rpcError: any = serializeError(e);
          // @ts-ignore
          rpcError.data && rpcError.data.originalError
            ? toastError(rpcError?.data?.originalError?.reason)
            : toastError(rpcError.message);
        } finally {
          if (tx) {
            dispatch(clearPendingTxn(tx.hash));
            // dispatch(success("Approve Success!"))
            toastSuccess("Approve Success!");
          }
        }
      }
    },
    [address, dispatch, provider]
  );

  return { getERC20Approved, approveToken };
}

export const useQueryApproved = (
  token?: string,
  spender?: string,
  inputValue_ELP?: string | number
) => {
  const { address, provider } = useWeb3Context();
  const updataBlock = useUpDataBlockNumber();
  const dispatch = useDispatch();
  const tokenContract = useTokenContract(token);
  const { result: routerApporve } = useSingleCallResult(
    tokenContract,
    "allowance",
    [address ? address : PLACEHOLDER_ACCOUNT, spender ?? undefined]
  );

  const approveToken = useCallback(async () => {
    if (provider && address && spender && tokenContract) {
      let tx;
      try {
        tx = await tokenContract.approve(spender, ethers.constants.MaxUint256);
        dispatch(
          fetchPendingTxns({
            txnHash: tx.hash,
            text: "Approve",
            type: "approveToken",
          })
        );
        const data = await tx.wait();
        updataBlock(data);
        return tx;
      } catch (e: unknown) {
        const rpcError: any = serializeError(e);
        // @ts-ignore
        rpcError.data && rpcError.data.originalError
          ? toastError(rpcError?.data?.originalError?.reason)
          : toastError(rpcError.message);
      } finally {
        if (tx) {
          dispatch(clearPendingTxn(tx.hash));
          // dispatch(success("Approve Success!"))
          toastSuccess("Approve Success!");
        }
      }
    }
  }, [address, dispatch, provider, spender, tokenContract, updataBlock]);

  const isApproved = useMemo(() => {
    if (!routerApporve || !inputValue_ELP) return false;
    const data = routerApporve[0];
    const mde = BN(inputValue_ELP).times(BN(10).pow(18));

    return mde.gt(data.toString());
  }, [inputValue_ELP, routerApporve]);
  const ApprovedNumber = useMemo(() => {
    if (!routerApporve) return;
    return routerApporve[0];
  }, [routerApporve]);
  return {
    Approved: isApproved,
    ApprovedNumber,
    approveToken,
  };
};
